// ============================================================================
// Top-level app shell: a grouped, role-aware sidebar + top bar with the user
// menu. Wraps all authenticated routes.
//
// Option D IA (2026-06-13): the nav renders from the shared nav-config tree —
// a pinned core, five flat ops modules, and three collapsible hubs (People ·
// Money · Admin). Hub rows bubble up the SUM of their children's "needs my
// attention" badges; collapse state persists per-hub in localStorage and a
// hub auto-expands when you're inside it.
//
// Visual treatment matches `02-shell-components.html`: warm stone canvas,
// slate ink, lucide iconography, slate-900 primary buttons.
// ============================================================================

import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FeedbackButton } from '../components/feedback/FeedbackButton';
import { Building2, ChevronDown, ChevronRight, LogOut, Search, UserPlus } from 'lucide-react';
import { useAuth } from './auth';
import { Button } from './components';
import { UserMenu } from './user-menu';
// item #650: the bell shows news only; the interruptive panel (./notification-bell) is unrouted for one release.
import NotificationBell from './news-bell';
import { PendingApproval } from './PendingApproval';
import { AccountDisabled } from './AccountDisabled';
import { VersionToast } from './version-check';
import {
  NAV,
  hubVisible,
  hubIndexVisible,
  visibleHubChildren,
  leafVisible,
  type NavHub,
  type NavLeaf,
} from './nav-config';
import { useNavBadges } from './use-nav-badges';
import { useCanAccessSourcing } from './use-can-access-sourcing';
import { MobileNav } from './mobile-nav';
import { CommandPalette } from './command-palette';

function openPalette() {
  window.dispatchEvent(new CustomEvent('tcs:open-palette'));
}

const HUB_COLLAPSE_LS_KEY = 'tcs:nav:hubs';

function loadHubState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(HUB_COLLAPSE_LS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function NavShell() {
  const { user, employee, profile, loading, signIn, signOut, error, errorCode } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Per-module badges. MUST be declared above any conditional early-return —
  // React's Rules of Hooks require hook calls to happen in the same order on
  // every render. The underlying hooks self-handle the no-auth case.
  const badges = useNavBadges();
  const { canAccess: canAccessSourcing } = useCanAccessSourcing();
  const [hubState, setHubState] = useState<Record<string, boolean>>(loadHubState);

  const badgeForLeaf = (leaf: NavLeaf): number =>
    leaf.badgeKey ? badges[leaf.badgeKey] : 0;

  // ----- Loading splash --------------------------------------------------
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-tcs-bg-muted text-sm text-tcs-text-muted">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-tcs-accent" />
          Loading session…
        </div>
      </div>
    );
  }

  // ----- Sign-in screen --------------------------------------------------
  if (!user) return <SignInScreen onSignIn={signIn} error={error} />;

  // ----- Employee-record gates (no_employee / inactive_employee) ---------
  // For these the bridge 403s BEFORE minting a JWT, so we arrive here with a
  // Firebase user but no bridged employee. `errorCode` (surfaced by the auth
  // context from the bridge's response `code`) tells the two modes apart so we
  // can give the user actionable copy instead of a generic failure. These stay
  // distinct from `pending` (awaiting approval) and `disabled` handled below.
  const hrMailto = (
    <a href="mailto:hr@terminalcleaning.com" className="font-medium underline">
      hr@terminalcleaning.com
    </a>
  );

  if (errorCode === 'inactive_employee') {
    return (
      <div className="flex h-full items-center justify-center bg-tcs-bg-muted p-6">
        <div className="w-full max-w-md rounded-xl border border-st-warn-border-strong bg-st-warn-bg/60 p-6 text-sm text-st-warn-fg-strong">
          <div className="flex items-start gap-3">
            <UserPlus className="mt-0.5 h-5 w-5 shrink-0 text-st-warn-fg" />
            <div className="space-y-2">
              <p className="font-semibold">Your employee record is inactive.</p>
              <p>
                Your TCS employee record is inactive. Contact HR ({hrMailto}) if
                this is a mistake.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOut().then(() => navigate('/'))}
                icon={<LogOut className="h-4 w-4" />}
                className="mt-2"
              >
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!employee) {
    // `no_employee` (explicit) or any other case where we can't resolve an
    // employee row for a signed-in domain user — direct them to HR. If the
    // bridge returned some other error code (e.g. token_expired), show the
    // raw message rather than misdirecting them to HR.
    const genericBridgeFailure =
      errorCode != null && errorCode !== 'no_employee';
    return (
      <div className="flex h-full items-center justify-center bg-tcs-bg-muted p-6">
        <div className="w-full max-w-md rounded-xl border border-st-warn-border-strong bg-st-warn-bg/60 p-6 text-sm text-st-warn-fg-strong">
          <div className="flex items-start gap-3">
            <UserPlus className="mt-0.5 h-5 w-5 shrink-0 text-st-warn-fg" />
            <div className="space-y-2">
              {genericBridgeFailure ? (
                <>
                  <p className="font-semibold">Sign-in couldn't be completed.</p>
                  <p>{error ?? 'Please try signing in again.'}</p>
                </>
              ) : (
                <>
                  <p className="font-semibold">
                    You don't have a TCS employee record yet.
                  </p>
                  <p>
                    You're signed in with Google, but you don't have a TCS
                    employee record yet. Contact HR ({hrMailto}) to have your
                    employee record created. Once it's built, you'll be able to
                    sign in.
                  </p>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOut().then(() => navigate('/'))}
                icon={<LogOut className="h-4 w-4" />}
                className="mt-2"
              >
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ----- Status gate (pending / disabled) --------------------------------
  if (profile?.status === 'disabled') {
    return (
      <AccountDisabled
        email={user.email ?? undefined}
        onSignOut={() => signOut().then(() => navigate('/'))}
      />
    );
  }
  if (profile?.status === 'pending') {
    return (
      <PendingApproval
        email={user.email ?? undefined}
        displayName={profile.display_name}
        onSignOut={() => signOut().then(() => navigate('/'))}
      />
    );
  }

  // ----- Authenticated shell ---------------------------------------------
  const role = profile?.role;
  const gates = { sourcing: canAccessSourcing };
  const fullName = `${employee.employee_name_first} ${employee.employee_name_last}`.trim();

  const isHubActive = (hub: NavHub): boolean =>
    location.pathname === hub.basePath || location.pathname.startsWith(`${hub.basePath}/`);

  const hubOpen = (hub: NavHub): boolean =>
    hub.key in hubState ? hubState[hub.key] : isHubActive(hub);

  const toggleHub = (hub: NavHub) => {
    setHubState((prev) => {
      const next = { ...prev, [hub.key]: !(hub.key in prev ? prev[hub.key] : isHubActive(hub)) };
      try {
        localStorage.setItem(HUB_COLLAPSE_LS_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota/availability errors */
      }
      return next;
    });
  };

  const leafBadge = (count: number) =>
    count > 0 ? (
      <span className="ml-auto rounded-full bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-semibold leading-none text-st-warn-fg">
        {count}
      </span>
    ) : null;

  const renderLeaf = (leaf: NavLeaf) => (
    <NavLink
      key={leaf.path}
      to={leaf.path}
      end={leaf.path === '/'}
      className={({ isActive }) =>
        [
          'mb-0.5 flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-tcs-surface font-medium text-tcs-text'
            : 'text-tcs-text-secondary hover:bg-tcs-bg-muted hover:text-tcs-text',
          leaf.status === 'soon' ? 'opacity-70' : '',
        ].join(' ')
      }
    >
      <leaf.icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{leaf.label}</span>
      {leafBadge(badgeForLeaf(leaf))}
      {leaf.status === 'soon' && (
        <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-tcs-text-muted">
          Soon
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="flex h-full bg-tcs-bg-muted">
      {/* Sidebar */}
      <aside className="hidden h-full w-64 shrink-0 flex-col border-r border-tcs-border bg-tcs-bg md:flex">
        <div className="flex h-16 shrink-0 items-center gap-2.5 px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-tcs-inverse text-tcs-inverse-ink">
            <Building2 className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold leading-tight text-tcs-text">TCS App</div>
            <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
              Terminal Cleaning
            </div>
          </div>
        </div>
        {/* ⌘K palette affordance (PR-3) */}
        <button
          type="button"
          onClick={openPalette}
          className="mx-3 mb-2 flex shrink-0 items-center gap-2 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-1.5 text-xs text-tcs-text-muted transition-colors hover:bg-tcs-surface"
          style={{ width: 'calc(100% - 1.5rem)' }}
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">Search or jump to…</span>
          <kbd className="ml-auto rounded border border-tcs-border bg-tcs-bg px-1.5 py-0.5 font-mono text-[10px] leading-none">
            ⌘K
          </kbd>
        </button>
        <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
          {NAV.map((node, i) => {
            if (node.kind === 'divider') {
              return <div key={`div-${i}`} className="my-2 border-t border-tcs-border" />;
            }
            if (node.kind === 'leaf') {
              return leafVisible(role, node, gates) ? renderLeaf(node) : null;
            }
            // hub
            if (!hubVisible(role, node, gates)) return null;
            const children = visibleHubChildren(role, node, gates);
            const hubBadge = children.reduce((s, c) => s + badgeForLeaf(c), 0);
            const open = hubOpen(node);
            return (
              <div key={node.key} className="mb-0.5">
                <button
                  type="button"
                  onClick={() => toggleHub(node)}
                  aria-expanded={open}
                  className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-tcs-text-secondary transition-colors hover:bg-tcs-bg-muted hover:text-tcs-text"
                >
                  <node.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate text-left">{node.label}</span>
                  {hubBadge > 0 && (
                    <span className="rounded-full bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-semibold leading-none text-st-warn-fg">
                      {hubBadge}
                    </span>
                  )}
                  {open ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
                  )}
                </button>
                {open && (
                  <div className="ml-4 border-l border-tcs-border pl-2">
                    {/* The hub's first tab, then one row per child module. A hub with
                        its own index tab (People's Desk, item 818) shows it only to the
                        roles that hold it and shows nobody an Overview. */}
                    {(node.index ? hubIndexVisible(role, node) : true) && (
                      <NavLink
                        to={node.basePath}
                        end
                        className={({ isActive }) =>
                          [
                            'mb-0.5 flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] transition-colors',
                            isActive
                              ? 'bg-tcs-surface font-medium text-tcs-text'
                              : 'text-tcs-text-secondary hover:bg-tcs-bg-muted hover:text-tcs-text',
                          ].join(' ')
                        }
                      >
                        <span className="flex-1 truncate">{node.index ? node.index.label : 'Overview'}</span>
                      </NavLink>
                    )}
                    {children.map((c) => (
                      <NavLink
                        key={c.path}
                        to={c.path}
                        className={({ isActive }) =>
                          [
                            'mb-0.5 flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] transition-colors',
                            isActive
                              ? 'bg-tcs-surface font-medium text-tcs-text'
                              : 'text-tcs-text-secondary hover:bg-tcs-bg-muted hover:text-tcs-text',
                          ].join(' ')
                        }
                      >
                        <span className="flex-1 truncate">{c.label}</span>
                        {leafBadge(badgeForLeaf(c))}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-[calc(4rem+env(safe-area-inset-top))] shrink-0 items-center justify-end gap-3 border-b border-tcs-border bg-tcs-bg px-4 pt-[env(safe-area-inset-top)] md:h-16 md:px-6">
          {/* Mobile-only logo */}
          <div className="mr-auto flex items-center gap-2 md:hidden">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-tcs-inverse text-tcs-inverse-ink">
              <Building2 className="h-3.5 w-3.5" />
            </div>
            <div className="text-sm font-semibold text-tcs-text">TCS App</div>
          </div>

          {/* CW-I Phase 1 — was a decorative <IconButton> with no onClick, no
              badge and no panel. Now the real bell: interruptive-unread count
              + a dropdown that deep-links, and never marks anything read just
              because you looked at it. */}
          <NotificationBell />

          {/* CW-J Phase 2 — the avatar is now a real menu. It was decorative
              (a circle with no onClick) and Sign out was a separate header
              button; Ben's gate decision put the Appearance control "in the
              avatar menu", so the menu had to exist. Sign out moved into it. */}
          <UserMenu
            fullName={fullName}
            role={role ?? null}
            email={user.email}
            onSignOut={() => signOut()}
          />
        </header>

        <main className="min-h-0 flex-1 overflow-auto bg-tcs-bg-muted">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar + "More" sheet (PR-2). Replaces the old
            horizontal chip scroll; hidden on md+ where the sidebar takes over. */}
        <MobileNav role={role} gates={gates} badges={badges} onSignOut={() => signOut()} />
      </div>
      {/* AFB-0020 — hide the floating Feedback pill on routes whose own primary
          control it would overlap: the Hiring "+" FAB, the Ask-AI (copilot)
          send/paper-airplane button, and the My Route "+" FAB. */}
      <FeedbackButton
        hideOnRoutes={['/people/hiring', '/hiring', '/hr-hiring', '/copilot', '/route']}
      />

      {/* ⌘K command palette (PR-3) — global; opened by ⌘K/Ctrl-K or the
          sidebar affordance (tcs:open-palette). */}
      <CommandPalette />

      {/* New-deploy detector → non-blocking "Reload" banner (cutover #3). */}
      <VersionToast />
    </div>
  );
}

// The app's own sign-in screen. NavShell shows it to anyone signed out; the
// Claude connector's consent screen (/build-room/connect, item #853) shows the
// same one, outside the shell, so a signed-out Ben signs in the usual way.
export function SignInScreen({ onSignIn, error }: { onSignIn: () => void; error?: string | null }) {
  return (
    <div className="flex h-full items-center justify-center bg-tcs-bg-muted p-4">
      <div className="w-full max-w-sm rounded-xl border border-tcs-border bg-tcs-bg p-8 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-tcs-inverse text-tcs-inverse-ink">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-tcs-text">TCS App</h1>
            <p className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
              Terminal Cleaning Services
            </p>
          </div>
        </div>
        <p className="mt-6 text-sm text-tcs-text-secondary">
          Sign in with your Terminal Cleaning Google account to continue.
        </p>
        <Button onClick={onSignIn} className="mt-5 w-full" icon={<GoogleIcon />}>
          Sign in with Google
        </Button>
        {error && (
          <div className="mt-4 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-rag-red">
            {error}
          </div>
        )}
        <p className="mt-6 text-center text-[11px] text-tcs-text-muted">
          Restricted to <span className="font-mono">@terminalcleaning.com</span> accounts.
        </p>
      </div>
    </div>
  );
}

// Inline Google "G" mark — keeps the sign-in button branded without pulling
// in another icon dependency.
function GoogleIcon() {
  return (
    <svg
      viewBox="0 0 18 18"
      className="h-4 w-4"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
